/**
 * /dashboard/accounting/reports/msme-overdue — MSME-overdue (G_ACC8).
 *
 * MSMED Act 2006 §15-16: bills owed to MSME-classified suppliers past
 * the 45-day window, with the v1 simple-interest estimator at 9% p.a.
 * The interest figure is a copy-ready estimator for the CA's working
 * paper; the §16 statutory rate (RBI bank rate × 3, compounded monthly)
 * is a year-end auditor adjustment.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/msme-overdue/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download, Info } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaise } from "@/lib/expense-format";
import { msmeOverdueCSVURL } from "@/lib/api/accounting";
import { useMSMEOverdueReport } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/msme-overdue",
)({
  component: MSMEOverdueRoute,
});

function todayUTCDate(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function MSMEOverdueRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [asOf, setAsOf] = useState<string>(todayUTCDate());
  const [pending, setPending] = useState<string>(asOf);
  const query = useMSMEOverdueReport(asOf);
  const data = query.data ?? null;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  return (
    <AccountingModuleShell
      title="MSME Overdue"
      description="Bills owed to MSME-classified suppliers past the 45-day clock."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <a href={msmeOverdueCSVURL(asOf)} download>
              <Download className="h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_auto]">
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="msme-asof">As of</Label>
            <Input
              id="msme-asof"
              type="date"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => setAsOf(pending)}
              disabled={!pending || pending === asOf}
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Alert className="mt-4">
        <Info className="h-4 w-4" />
        <AlertTitle>About the interest column</AlertTitle>
        <AlertDescription>
          The v1 estimator uses 9% p.a. simple interest. MSMED Act §16 mandates
          three times the RBI bank rate compounded monthly — your CA will
          adjust at year-end. The principal × days breakdown here is suitable
          for the working paper.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load MSME-overdue report</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-4">
          <Card>
            <CardContent className="grid gap-2 py-4 text-sm sm:grid-cols-3">
              <div>
                <span className="text-muted-foreground">Overdue bills</span>
                <p className="text-base font-medium">{data.count}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total outstanding</span>
                <p className="text-base font-medium tabular-nums">
                  {formatPaise(data.total_outstanding_minor)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">
                  Total interest (estimator)
                </span>
                <p className="text-base font-medium tabular-nums">
                  {formatPaise(data.total_interest_minor)}
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Udyam</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Days overdue</TableHead>
                  <TableHead className="text-right">Interest @9% pa</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      No MSME bills overdue as of {data.as_of}.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.rows.map((r) => (
                    <TableRow key={r.bill_id}>
                      <TableCell className="font-mono text-xs">
                        {r.bill_no}
                      </TableCell>
                      <TableCell>{r.vendor_name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.vendor_msme_udyam || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(r.outstanding_minor)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.days_overdue}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(r.interest_minor)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
